/**
 * useFicheAgent.ts — SSE version
 * Remplace WebSocket par EventSource pour compatibilité GKE/GCE.
 * Interface identique à l'ancienne version (send, reset, agentInfo, status).
 */

import { useState, useRef, useCallback, useEffect } from 'react';

const API_BASE = (import.meta as any).env?.VITE_API_URL || '/api';

export interface ChatMessage {
  id:        string;
  role:      'user' | 'assistant';
  content:   string;
  streaming?: boolean;
}

export interface ThinkingStep {
  type:     'tool_call' | 'tool_result';
  tool?:    string;
  input?:   string;
  content?: string;
}

export interface AgentInfo {
  agent_name?:  string;
  agent_image?: string | null;
  icon_emoji?:  string | null;
  domaine?:     string | null;
}

export type AgentStatus = 'idle' | 'connecting' | 'connected' | 'thinking' | 'error';

/**
 * ficheId : agent de la fiche (contexte = la décision).
 * agentSlug : conversation directe avec un agent, sans fiche (accueil desktop).
 */
export function useFicheAgent(ficheId: string, uid: string = '', agentSlug?: string) {
  const [messages,   setMessages]   = useState<ChatMessage[]>([]);
  const [steps,      setSteps]      = useState<ThinkingStep[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [status,     setStatus]     = useState<AgentStatus>('idle');
  const [agentInfo,  setAgentInfo]  = useState<AgentInfo | null>(null);

  const sessionId = useRef<string>(crypto.randomUUID());
  const esRef     = useRef<EventSource | null>(null);
  const streamRef = useRef<string>('');

  // Charger les infos de l'agent au montage
  useEffect(() => {
    if (agentSlug) {
      fetch(`${API_BASE}/fiches-agent/agents`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          const a = data?.agents?.find((x: { slug: string }) => x.slug === agentSlug);
          if (a) setAgentInfo({ agent_name: a.name, agent_image: a.image, icon_emoji: a.icon_emoji, domaine: a.domaines?.[0] });
        })
        .catch(() => {});
      return;
    }
    fetch(`${API_BASE}/fiches-agent/${ficheId}/agent-info`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAgentInfo(data); })
      .catch(() => {});
  }, [ficheId, agentSlug]);

  useEffect(() => {
    return () => { esRef.current?.close(); };
  }, []);

  const send = useCallback((question: string, reset = false) => {
    esRef.current?.close();
    esRef.current = null;

    const userMsg: ChatMessage = {
      id:      crypto.randomUUID(),
      role:    'user',
      content: question,
    };
    setMessages(prev => [...prev, userMsg]);
    setSteps([]);
    setIsThinking(true);
    setStatus('connecting');
    setError(null);
    streamRef.current = '';

    const assistantId = crypto.randomUUID();
    const params = new URLSearchParams({
      question,
      uid,
      session_id: sessionId.current,
      reset:      reset ? 'true' : 'false',
    });
    const url = agentSlug
      ? `${API_BASE}/fiches-agent/agent/${agentSlug}/chat/stream?${params}`
      : `${API_BASE}/fiches-agent/${ficheId}/chat/stream?${params}`;
    console.log('[SSE] Connexion →', url);

    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (e) => {
      let event: Record<string, any>;
      try { event = JSON.parse(e.data); } catch { return; }

      const type = event.type;

      if (type === 'connected') {
        setStatus('thinking');
        // Récupérer agent_info depuis l'event si disponible
        if (event.agent_info) setAgentInfo(event.agent_info);
        return;
      }

      if (type === 'token') {
        const chunk = (event.content ?? '') as string;
        streamRef.current += chunk;
        const current = streamRef.current;
        setMessages(prev => {
          const exists = prev.find(m => m.id === assistantId);
          if (exists) {
            return prev.map(m =>
              m.id === assistantId ? { ...m, content: current, streaming: true } : m
            );
          }
          return [...prev, { id: assistantId, role: 'assistant', content: current, streaming: true }];
        });
      }

      else if (type === 'tool_call') {
        if (event.tool && event.tool !== 'final_answer') {
          setSteps(prev => [...prev, { type: 'tool_call', tool: event.tool, input: event.input }]);
        }
      }

      else if (type === 'tool_result') {
        setSteps(prev => [...prev, { type: 'tool_result', content: event.content }]);
      }

      else if (type === 'done') {
        const final = (event.content ?? streamRef.current) as string;
        setMessages(prev => {
          const exists = prev.find(m => m.id === assistantId);
          if (exists) {
            return prev.map(m =>
              m.id === assistantId ? { ...m, content: final, streaming: false } : m
            );
          }
          return [...prev, { id: assistantId, role: 'assistant', content: final }];
        });
        setIsThinking(false);
        setStatus('connected');
        es.close();
        esRef.current = null;
      }

      else if (type === 'error') {
        setError(event.content ?? 'Erreur inconnue');
        setIsThinking(false);
        setStatus('error');
        es.close();
        esRef.current = null;
      }

      else if (type === 'end') {
        setIsThinking(false);
        setStatus('connected');
        es.close();
        esRef.current = null;
      }
    };

    es.onerror = () => {
      if (esRef.current?.readyState === EventSource.CLOSED) {
        setIsThinking(false);
        setStatus('error');
        setError('Connexion perdue');
      }
    };

  }, [ficheId, agentSlug]);

  const reset = useCallback(() => {
    esRef.current?.close();
    esRef.current = null;
    sessionId.current = crypto.randomUUID();
    setMessages([]);
    setSteps([]);
    setIsThinking(false);
    setStatus('idle');
    setError(null);
    streamRef.current = '';
  }, []);

  return { messages, steps, isThinking, error, status, agentInfo, send, reset };
}